import { TranslationWidth } from '@angular/common';
import { Component, EventEmitter, Injectable, Input, OnInit, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { NgbCalendar, NgbDate, NgbDateParserFormatter, NgbDatepickerI18n, NgbDateStruct, NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';
import moment, { Moment } from 'moment';
import { TranslateService } from '@ngx-translate/core';

const I18N_VALUES = {
  weekdays: [
    'common.datePicker.monday',
    'common.datePicker.tuesday',
    'common.datePicker.wednesday',
    'common.datePicker.thursday',
    'common.datePicker.friday',
    'common.datePicker.saturday',
    'common.datePicker.sunday'
  ],
  months: [
    'common.datePicker.january',
    'common.datePicker.february',
    'common.datePicker.march',
    'common.datePicker.april',
    'common.datePicker.may',
    'common.datePicker.june',
    'common.datePicker.july',
    'common.datePicker.august',
    'common.datePicker.september',
    'common.datePicker.october',
    'common.datePicker.november',
    'common.datePicker.december'
  ]
};
@Injectable()
export class CustomDatepickerI18n extends NgbDatepickerI18n {
  constructor(private _translate: TranslateService) {
    super();
  }
  getWeekdayLabel(weekday: number): string {
    return this._translate.instant(I18N_VALUES.weekdays[weekday - 1]);
  }
  getWeekdayShortName(weekday: number): string {
    return this._translate.instant(I18N_VALUES.weekdays[weekday - 1]);
  }
  getMonthShortName(month: number): string {
    return this._translate.instant(I18N_VALUES.months[month - 1]);
  }
  getMonthFullName(month: number): string {
    return this.getMonthShortName(month);
  }
  getDayAriaLabel(date: NgbDateStruct): string {
    return `${date.day}-${date.month}-${date.year}`;
  }
}

@Component({
  selector: 'app-date-picker-selector',
  templateUrl: './date-picker-selector.component.html',
  styleUrls: ['./date-picker-selector.component.css'],
  providers: [[{provide: NgbDatepickerI18n, useClass: CustomDatepickerI18n}]]
})

export class DatePickerSelectorComponent implements OnInit {
  @Input() startDate: Moment
  @Input() endDate: Moment
  @Input() isSingleDate: boolean = false;
  @Input() allowFutureDates: boolean = false;
  @Input() allowPastDates: boolean = true;
  @Input() form: UntypedFormGroup;

  // Hide specific buttons on the date picker.
  @Input() btnTodayEnabled: boolean = true;
  @Input() btnYesterdayEnabled: boolean = true;
  @Input() btnThisWeekEnabled: boolean = true;
  @Input() btnLastWeekEnabled: boolean = true;
  @Input() btnThisMonthEnabled: boolean = true;
  @Input() btnLastMonthEnabled: boolean = true;
  @Input() btnLastQuarterEnabled: boolean = true;

  @Output() applyDateChange :EventEmitter<any> = new EventEmitter<any>();

  hoveredDate: NgbDate | null = null;
  fromDate: NgbDate | null;
  toDate: NgbDate | null;
  minDate: NgbDate | null;
  maxDate: NgbDate | null;
  dayFormat: TranslationWidth = TranslationWidth.Abbreviated

  firstBtnRowEnabled: boolean = (this.btnTodayEnabled || this.btnYesterdayEnabled || this.btnThisWeekEnabled || this.btnLastWeekEnabled);
  secondBtnRowEnabled: boolean = (this.btnThisMonthEnabled || this.btnLastMonthEnabled || this.btnLastQuarterEnabled);

  isDisabled = (date: NgbDate, current: { month: number; year: number}) => date.month !== current.month;

  constructor(
    public i18n: NgbDatepickerI18n,
    private calendar: NgbCalendar,
    public formatter: NgbDateParserFormatter) {
      this.minDate = calendar.getToday();
      this.maxDate = calendar.getToday();
  }

  ngOnInit(): void {
    this.fromDate = new NgbDate(this.startDate.year(), this.startDate.month() + 1, this.startDate.date());
    this.toDate = new NgbDate(this.endDate.year(), this.endDate.month() + 1, this.endDate.date());

    if (this.isSingleDate) {
      this.form.controls.date.setValue(this.formatDates());
    } else {
      this.form.controls.dateRange.setValue(this.formatDates());
    }

    // Set the max date to today + 1 year if we are allowing future dates.
    if (this.allowFutureDates) {
      this.maxDate = new NgbDate(this.startDate.year() + 1, this.startDate.month() + 1, this.startDate.date());
    } else {
      this.maxDate = this.calendar.getToday();
    }

    this.isMinDateAllowed();
  }

  onDateSelection(date: NgbDate) {
    if (this.isSingleDate) {
      this.toDate = date;
      this.fromDate = date;
    } else {
      if (!this.fromDate && !this.toDate) {
        this.fromDate = date;
      } else if (this.fromDate && !this.toDate && date && date.after(this.fromDate)) {
        this.toDate = date;
      } else {
        this.toDate = null;
        this.fromDate = date;
      }
    }

    if (this.isSingleDate) {
      this.form.controls.date.setValue(this.formatDates());
    } else {
      this.form.controls.dateRange.setValue(this.formatDates());
    }
  }

  isHovered(date: NgbDate) {
    return (
      this.fromDate && !this.toDate && this.hoveredDate && date.after(this.fromDate) && date.before(this.hoveredDate)
    );
  }

  isInside(date: NgbDate) {
    return this.toDate && date.after(this.fromDate) && date.before(this.toDate);
  }

  isRange(date: NgbDate) {
    return (
      date.equals(this.fromDate) ||
      (this.toDate && date.equals(this.toDate)) ||
      this.isInside(date) ||
      this.isHovered(date)
    );
  }

  isMinDateAllowed() {
    // Set the min date to today if we are not allowing past dates.
    if (!this.allowPastDates) {
      this.minDate = this.calendar.getToday();
    } else {
      this.minDate = new NgbDate(this.startDate.year() - 5, this.startDate.month(), this.startDate.date());
    }
  }

  formatDates(): string {
    if (this.isSingleDate) {
      return moment(new Date(this.fromDate.year, this.fromDate.month - 1, this.fromDate.day)).format("DD/MM/YYYY");
    } else {
      if (this.toDate) {
        return moment(new Date(this.fromDate.year, this.fromDate.month - 1, this.fromDate.day)).format("DD/MM/YYYY") + " - " + moment(new Date(this.toDate.year, this.toDate.month - 1, this.toDate.day)).format("DD/MM/YYYY")
      } else {
        return moment(new Date(this.fromDate.year, this.fromDate.month - 1, this.fromDate.day)).format("DD/MM/YYYY") + " - " + moment(new Date(this.fromDate.year, this.fromDate.month - 1, this.fromDate.day)).format("DD/MM/YYYY")
      }
    }
  }

  setPresetDate(datepicker: NgbInputDatepicker, dateType: number) {
    var now = moment();

    switch (dateType) {
      case 0:
        this.fromDate = this.calendar.getToday();
        this.toDate = this.calendar.getToday();
        datepicker.navigateTo(this.calendar.getToday());
        break;
      case 1:
        this.fromDate = this.calendar.getPrev(this.calendar.getToday(), 'd', 1);
        this.toDate = this.calendar.getPrev(this.calendar.getToday(), 'd', 1);
        datepicker.navigateTo(this.calendar.getToday());
        break;
      case 2:
        const currentWeekMonday = moment().isoWeekday(1);
        this.fromDate = new NgbDate(currentWeekMonday.year(), currentWeekMonday.month() + 1, currentWeekMonday.date());
        this.toDate = this.calendar.getToday();
        datepicker.navigateTo(this.fromDate);
        break;
      case 3:
        const prevWeekMonday = moment().subtract(1, 'weeks').startOf('isoWeek');
        const prevWeekSunday = moment().subtract(1, 'weeks').endOf('isoWeek');
        this.fromDate = new NgbDate(prevWeekMonday.year(), prevWeekMonday.month() + 1, prevWeekMonday.date());
        this.toDate = new NgbDate(prevWeekSunday.year(), prevWeekSunday.month() + 1, prevWeekSunday.date());
        datepicker.navigateTo(this.fromDate);
        break;
      case 4:
        this.fromDate = new NgbDate(this.calendar.getToday().year, this.calendar.getToday().month, moment().startOf('month').date());
        this.toDate = this.calendar.getToday();
        datepicker.navigateTo(this.calendar.getToday());
        break;
      case 5:
        var lastMonth = now.clone().subtract(1, 'months');

        this.fromDate = new NgbDate(lastMonth.year(), lastMonth.month() + 1, lastMonth.startOf('month').date());
        this.toDate = new NgbDate(lastMonth.year(), lastMonth.month() + 1, lastMonth.endOf('month').date());
        datepicker.navigateTo(this.calendar.getPrev(this.calendar.getToday(), 'm', 2));
        break;
      case 6:
        this.fromDate = this.calendar.getPrev(this.calendar.getToday(), 'm', 3);
        this.toDate = this.calendar.getToday();
        datepicker.navigateTo(this.calendar.getPrev(this.calendar.getToday(), 'm', 3));
        break;
      default:
        break;
    }

    if (!this.isSingleDate) {
      this.form.controls.dateRange.setValue(this.formatDates());
    }
  }

  close(datepicker: NgbInputDatepicker) {
    datepicker.close()
  }

  apply(datepicker: NgbInputDatepicker) {
    this.applyDateChange.emit(null);
    this.isMinDateAllowed();
    datepicker.close();
  }

  reset() {
    this.fromDate = new NgbDate(this.startDate.year(), this.startDate.month() + 1, this.startDate.date());
    this.toDate = new NgbDate(this.endDate.year(), this.endDate.month() + 1, this.endDate.date());
	}

  updateFormDateRange() {
    this.form.controls.dateRange.setValue(this.formatDates());
  }
}
